import React from 'react'
import { useNavigate } from 'react-router-dom'
import { OrdersContext } from 'contexts/OrdersContext'
import jsQR from 'jsqr'
import _ from 'lodash'
import { PixelFactoryContext } from 'pixel-base'
import { useApi } from 'shared/hooks'
import {
  createPurchaseOrder,
  getCategories,
  getPurchaseDetails,
  getSuppliers,
  updatePurchaseDetail,
  updatePurchaseStatus,
  validateProducts,
} from 'utils/api-routes.util'
import * as XLSX from 'xlsx'

import {
  IPixelFactory,
  IStoreCollection,
  IWarehouse,
  ITenantInfo,
  IPurchaseDetails,
  IPurchaseItems,
  IValidatedProduct,
  IInvalidProduct,
  ISupplier,
} from '../../../../../../../decl'

export const usePurchaseEdit = (
  purchase_id: string,
  warehouse_id: string,
  setShowDetailModal: (value: boolean) => void,
  setPurchaseId: (value: string) => void,
  detailType: string,
  setDetailType: (value: string) => void,
  setReload: (value: boolean) => void,
) => {
  const { state } = React.useContext(OrdersContext)
  const navigate = useNavigate()
  const {
    showSuccess,
    showFailure,
    pixelConfirm,
    hidePageLoader,
    showPageLoader,
    showLoader,
    hideLoader,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)

  const [formEditAble, setFormEditAble] = React.useState(false)
  const [showScanner, setShowScanner] = React.useState(false)
  const [showFileUpload, setShowFileUpload] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [scannedSKU, setScannedSKU] = React.useState('')
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [uploadResults, setUploadResults] = React.useState<{
    validProducts: IValidatedProduct[]
    invalidProducts: IInvalidProduct[]
    successCount: number
    errorCount: number
    successRate: number
  } | null>(null)

  const tenant_info: ITenantInfo = JSON.parse(
    localStorage.getItem('tenant_info') || '{}',
  )
  const [addNewSupplierModal, setAddNewSupplierModal] = React.useState(false)
  const purchaseRef = React.useRef(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const scannerRef = React.useRef<HTMLVideoElement>(null)
  const [showSearchProduct, setShowSearchProduct] = React.useState(false)
  const [suppliers, setSuppliers] = React.useState<ISupplier[]>([])

  const [searchValue, setSearchValue] = React.useState('')
  const [purchaseDetail, setPurchaseDetail] = React.useState<IPurchaseDetails>({
    id: 0,
    ems_customer_id: 0,
    reference_number: '',
    total_amount: null,
    notes: '',
    user: 0,
    created_at: {
      date: '',
      timezone_type: 0,
      timezone: '',
    },
    is_final_status: false,
    created_by: 0,
    created_by_id: null,
    supplier: {
      id: 0,
      supplier_name: '',
      contact_person: '',
      phone: '',
    },
    purchase_status: {
      id: 0,
      status: '',
      description: '',
      is_final_status: false,
      is_default: false,
    },
    warehouse: {
      wh_id: 0,
      name: '',
    },
    purchase_items: [],
    type: '',
  })
  const [warehouseList, setWarehouseList] = React.useState<IWarehouse[]>([])
  const [newPurchase, setNewPurchase] = React.useState<IPurchaseDetails>({
    id: 0,
    ems_customer_id: 0,
    reference_number: '',
    total_amount: null,
    notes: '',
    user: 0,
    created_at: {
      date: '',
      timezone_type: 0,
      timezone: '',
    },
    is_final_status: false,
    created_by: 0,
    created_by_id: null,
    supplier: {
      id: 0,
      supplier_name: '',
      contact_person: '',
      phone: '',
    },
    purchase_status: {
      id: 0,
      status: '',
      description: '',
      is_final_status: false,
      is_default: false,
    },
    warehouse: {
      wh_id: warehouse_id ? Number(warehouse_id) : 0,
      name: '',
    },
    purchase_items: [],
    type: 'new',
  })

  const [error, setError] = React.useState({
    error: '',
    field: '',
  })

  const [categories, setCategories] = React.useState([])

  // API hooks
  const { data: categoriesData, loading: categoriesLoading } = useApi<{
    data: IStoreCollection[]
  }>(() => getCategories(), {
    onSuccess: (data) => {
      setCategories(
        data.data.map((item) => ({
          label: item.collection_id.source_collection_name,
          value: item.collection_id.source_collection_id,
        })),
      )
    },
    onError: (error: string) => {
      showFailure('Error loading categories')
      console.error('Categories error:', error)
    },
    skip: false,
  })

  const { data: suppliersData, loading: suppliersLoading } = useApi<{
    data: ISupplier[]
  }>(() => getSuppliers(), {
    onSuccess: (data) => {
      setSuppliers(data.data || [])
    },
    onError: (error: string) => {
      showFailure('Error loading suppliers')
      console.error('Suppliers error:', error)
    },
    skip: false,
  })

  const { data: purchaseDetailsData, loading: purchaseDetailsLoading } =
    useApi<{ data: IPurchaseDetails }>(() => getPurchaseDetails(purchase_id), {
      onSuccess: (data) => {
        setPurchaseDetail(data.data)
        if (
          data.data?.purchase_status?.status === 'Received' ||
          data.data?.purchase_status?.status === 'Completed'
        ) {
          navigate('/inventory/purchases')
        }
      },
      onError: (error: string) => {
        showFailure('Error loading purchase details')
        console.error('Purchase details error:', error)
      },
      skip: !purchase_id,
      deps: [purchase_id],
    })

  // Scanner functionality
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      if (scannerRef.current) {
        scannerRef.current.srcObject = stream
        scannerRef.current.onloadedmetadata = () => {
          startScanning()
        }
      }
    } catch (err) {
      showFailure('Unable to access camera')
      console.error('Camera access error:', err)
    }
  }

  const startScanning = () => {
    if (!scannerRef.current) return

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return

    const scanFrame = () => {
      if (!scannerRef.current || !context) return

      const video = scannerRef.current
      const { videoWidth, videoHeight } = video

      if (videoWidth === 0 || videoHeight === 0) {
        requestAnimationFrame(scanFrame)
        return
      }

      canvas.width = videoWidth
      canvas.height = videoHeight

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, videoWidth, videoHeight)

      // Get image data for scanning
      const imageData = context.getImageData(0, 0, videoWidth, videoHeight)

      // Scan for QR codes and barcodes
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      if (code) {
        // Found a code, process it
        setScannedSKU(code.data)
        setShowScanner(false)
        stopScanner()

        if (code.data) {
          validateScannedProduct(code.data)
        }
        return
      }

      // Continue scanning
      requestAnimationFrame(scanFrame)
    }

    scanFrame()
  }

  const stopScanner = () => {
    if (scannerRef.current && scannerRef.current.srcObject) {
      const stream = scannerRef.current.srcObject as MediaStream
      stream.getTracks().forEach((track) => track.stop())
    }
  }

  const handleScan = (event: React.ChangeEvent<HTMLInputElement>) => {
    const scannedData = event.target.value
    setScannedSKU(scannedData)
    setShowScanner(false)
    stopScanner()

    if (scannedData) {
      validateScannedProduct(scannedData)
    }
  }

  const validateScannedProduct = (sku: string) => {
    const storeId = 603

    showPageLoader()
    validateProducts({
      product_skus: [sku],
      store_id: storeId,
    }).subscribe(
      (res) => {
        const response = res as {
          response: {
            data: IValidatedProduct[]
            valid_products: IValidatedProduct[]
            _meta: {
              found_count: number
              error_count: number
              success_rate: number
            }
          }
        }
        hidePageLoader()

        if (response.response?.data) {
          const validationResult = response.response
          const validProducts = validationResult.valid_products || []

          if (validProducts.length > 0) {
            const validProduct = validProducts[0]

            const productToAdd = {
              id: null,
              product: {
                product_id: validProduct.product_id,
                product_image_path: validProduct.product_image_path,
                product_name: validProduct.product_name,
                product_sku: validProduct.product_sku,
              },
              accepted_count: null,
              damaged_count: null,
              received_count: 1,
              product_image_path: validProduct.product_image_path,
              purchase_price: 0,
              created_at: {
                date: new Date().toISOString(),
                timezone_type: 0,
                timezone: '',
              },
            }

            setNewPurchase((prev) => ({
              ...prev,
              purchase_items: [
                ...prev.purchase_items,
                productToAdd as IPurchaseItems,
              ],
            }))

            showSuccess(
              `Product "${validProduct.product_name}" added successfully`,
            )
          } else {
            showFailure(`Product with SKU "${sku}" not found`)
          }
        } else {
          showFailure('Error validating scanned product')
        }
      },
      (error) => {
        hidePageLoader()
        showFailure('Error validating scanned product')
        console.error('Scan validation error:', error)
      },
    )
  }

  // File upload functionality
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setUploadedFile(file)
        setShowFileUpload(true)
        processUploadedFile(file)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        showFailure('Please upload a CSV or Excel file')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }

  const handleFileUploadFromModal = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0]
      if (
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        setUploadedFile(file)
        setShowUploadModal(false)
        setShowFileUpload(true)
        processUploadedFile(file)
      } else {
        showFailure('Please upload a CSV or Excel file')
      }
    }
  }

  const downloadTemplate = () => {
    // Create template data
    const templateData = [
      { product_sku: 'SKU001', stock_count: 10, purchase_price: 25.99 },
      { product_sku: 'SKU002', stock_count: 5, purchase_price: 15.5 },
      { product_sku: 'SKU003', stock_count: 20, purchase_price: 8.75 },
    ]

    // Create Excel file
    const ws = XLSX.utils.json_to_sheet(templateData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')

    // Download Excel file
    XLSX.writeFile(wb, 'purchase_upload_template.xlsx')
  }

  const processUploadedFile = (file: File) => {
    const isCSV = file.type === 'text/csv'
    const isExcel =
      file.type === 'application/vnd.ms-excel' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    if (isCSV) {
      // Process CSV file
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string
          const lines = content.split('\n')
          const products: {
            product_sku: string
            quantity: number
            price: number
          }[] = []

          // Parse header to find column indices
          const headerLine = lines[0]?.trim()
          if (!headerLine) {
            showFailure('Invalid CSV file: missing header row')
            return
          }

          const headers = headerLine
            .split(',')
            .map((h) => h.trim().toLowerCase())
          const skuIndex = headers.findIndex(
            (h) => h === 'product_sku' || h === 'sku' || h === 'product sku',
          )
          const quantityIndex = headers.findIndex(
            (h) =>
              h === 'stock_count' || h === 'quantity' || h === 'stock count',
          )
          const priceIndex = headers.findIndex(
            (h) =>
              h === 'purchase_price' || h === 'price' || h === 'purchase price',
          )

          if (skuIndex === -1) {
            showFailure('Invalid CSV file: missing product_sku column')
            return
          }

          if (quantityIndex === -1) {
            showFailure('Invalid CSV file: missing stock_count/quantity column')
            return
          }

          // Process data rows
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim()
            if (line) {
              const columns = line.split(',')
              const product_sku = columns[skuIndex]?.trim()
              const stock_count = columns[quantityIndex]?.trim()
              const purchase_price =
                priceIndex !== -1 ? columns[priceIndex]?.trim() : '0'

              if (product_sku && stock_count) {
                products.push({
                  product_sku: product_sku,
                  quantity: parseInt(stock_count) || 1,
                  price: parseFloat(purchase_price) || 0,
                })
              }
            }
          }

          if (products.length === 0) {
            showFailure('No valid products found in the CSV file')
            return
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing CSV file')
          console.error('CSV processing error:', error)
        }
      }
      reader.readAsText(file)
    } else if (isExcel) {
      // Process Excel file
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<
            string,
            any
          >[]

          if (jsonData.length === 0) {
            showFailure('The uploaded Excel file is empty.')
            return
          }

          const products: {
            product_sku: string
            quantity: number
            price: number
          }[] = []

          jsonData.forEach((row: Record<string, any>) => {
            // Handle different possible column names
            const productSkuKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'product_sku' ||
                key.toLowerCase() === 'sku' ||
                key.toLowerCase() === 'product sku',
            )

            const quantityKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'stock_count' ||
                key.toLowerCase() === 'quantity' ||
                key.toLowerCase() === 'stock count',
            )

            const priceKey = Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'purchase_price' ||
                key.toLowerCase() === 'price' ||
                key.toLowerCase() === 'purchase price',
            )

            if (productSkuKey && quantityKey) {
              const product_sku = String(row[productSkuKey]).trim()
              const quantity = parseInt(String(row[quantityKey])) || 1
              const price = priceKey
                ? parseFloat(String(row[priceKey])) || 0
                : 0

              if (product_sku) {
                products.push({
                  product_sku,
                  quantity,
                  price,
                })
              }
            }
          })

          if (products.length === 0) {
            showFailure(
              'No valid products found in the Excel file. Please ensure the file has the correct column headers.',
            )
            return
          }

          validateProductsFromAPI(products)
        } catch (error) {
          showFailure('Error processing Excel file')
          console.error('Excel processing error:', error)
        }
      }
      reader.readAsArrayBuffer(file)
    } else {
      showFailure('Unsupported file type. Please upload a CSV or Excel file.')
    }
  }

  const validateProductsFromAPI = (
    products: { product_sku: string; quantity: number; price: number }[],
  ) => {
    const productSkus = products.map((p) => p.product_sku)
    const storeId = tenant_info?.store_id || 1

    setUploadProgress(25)
    showPageLoader()
    validateProducts({
      product_skus: productSkus,
      store_id: storeId,
    }).subscribe(
      (res) => {
        const response = res as {
          response: {
            data: (IValidatedProduct | IInvalidProduct)[]
            valid_products: IValidatedProduct[]
            _meta: {
              found_count: number
              error_count: number
              success_rate: number
            }
          }
        }
        hidePageLoader()
        setUploadProgress(75)

        if (response.response?.data) {
          const validationResult = response.response
          const validProducts = validationResult.valid_products || []
          const allProducts = validationResult.data || []

          const successCount = validationResult._meta?.found_count || 0
          const errorCount = validationResult._meta?.error_count || 0
          const successRate = validationResult._meta?.success_rate || 0

          const invalidProducts = allProducts.filter(
            (p): p is IInvalidProduct => 'status' in p && p.status === 'error',
          )

          setUploadResults({
            validProducts,
            invalidProducts,
            successCount,
            errorCount,
            successRate,
          })

          if (successCount > 0) {
            showSuccess(
              `Successfully validated ${successCount} products (${successRate.toFixed(
                1,
              )}% success rate)`,
            )

            const productsToAdd = validProducts.map(
              (validProduct: IValidatedProduct) => {
                const originalProduct = products.find(
                  (p) => p.product_sku === validProduct.product_sku,
                )
                return {
                  id: null,
                  product: {
                    product_id: validProduct.product_id,
                    product_image_path: validProduct.product_image_path,
                    product_name: validProduct.product_name,
                    product_sku: validProduct.product_sku,
                  },
                  accepted_count: null,
                  damaged_count: null,
                  received_count: originalProduct?.quantity || 1,
                  product_image_path: validProduct.product_image_path,
                  purchase_price: originalProduct?.price || 0,
                  created_at: {
                    date: new Date().toISOString(),
                    timezone_type: 0,
                    timezone: '',
                  },
                }
              },
            )

            setNewPurchase((prev) => ({
              ...prev,
              purchase_items: [
                ...prev.purchase_items,
                ...(productsToAdd as IPurchaseItems[]),
              ],
            }))
          } else {
            showFailure('No valid products found in the uploaded file')
          }

          setUploadProgress(100)
        } else {
          showFailure('Error validating products')
          setUploadProgress(0)
        }
      },
      (error) => {
        hidePageLoader()
        showFailure('Error validating products')
        setUploadProgress(0)
        console.error('Validation error:', error)
      },
    )
  }

  const getAllCategories = () => {
    // Trigger the categories API call
    // The useApi hook will handle the rest
  }

  const TotalCost = () => {
    const value = newPurchase.purchase_items
      .reduce((acc, curr) => {
        return acc + Number(curr?.received_count) * Number(curr?.purchase_price)
      }, 0)
      .toFixed(2)
    return value || '--'
  }

  const updatePurchase = () => {
    const data = {
      warehouse_id: newPurchase.warehouse?.id,
      products: newPurchase.purchase_items.map((item) => {
        return {
          ...item,
          product_id: item.product?.product_id,
          purchase_item_id: item.id,
          variation_id: null,
          stock_quantity: 0,
          sku: item.product?.product_sku,
          quantity: Number(item.received_count),
          purchase_price: item.purchase_price ? item.purchase_price : 0,
        }
      }),
      reference_number: newPurchase.reference_number,
      supplier_id: newPurchase.supplier?.id,
      products_total: TotalCost(),
      notes: newPurchase.notes,
      type: 'update',
    }
    showPageLoader()
    updatePurchaseDetail(purchase_id, data).subscribe((res) => {
      const response = res.response as {
        data: IPurchaseDetails
        error:
          | string
          | {
              error: string
              details?: Array<{ product_id: number; errors: string[] }>
            }
        field: string
      }
      hidePageLoader()
      if (response.data) {
        showSuccess('Purchase updated successfully')
        setPurchaseDetail(response.data)
        setFormEditAble(false)
        setDetailType('return')
        setError({ error: '', field: '' })
      } else {
        if (response.error) {
          let errorMessage = ''
          const errorField = response.field || ''

          // Handle complex error structure with details
          if (typeof response.error === 'object' && response.error.details) {
            errorMessage = response.error.error || 'Validation failed'
            const details = response.error.details
              .map(
                (detail) =>
                  `Product ID ${detail.product_id}: ${detail.errors.join(
                    ', ',
                  )}`,
              )
              .join('; ')
            errorMessage += ` - ${details}`
          } else if (typeof response.error === 'object') {
            errorMessage = response.error.error || 'An error occurred'
          } else {
            errorMessage = response.error
          }

          setError({
            error: errorMessage,
            field: errorField,
          })

          if (errorField) {
            showFailure(`${errorField}: ${errorMessage}`)
          } else {
            showFailure(errorMessage)
          }
        }
      }
    })
  }

  const updatePurchasesStatus = (purchase_id, status, message) => {
    pixelConfirm({
      title: 'Confirmation',
      message: `${message}`,
      onConfirm: () => {
        showPageLoader()
        updatePurchaseStatus(purchase_id, status).subscribe((res) => {
          const response = res.response as { message: string; error?: string }
          hidePageLoader()
          if (response.message) {
            setReload(true)
            showSuccess('Purchase status updated successfully')
            setDetailType('return')
            setPurchaseId(purchase_id)
            setShowDetailModal(false)
          } else {
            showFailure(response.error || 'Something went wrong')
          }
        })
      },
    })
  }

  const fetchPurchaseDetails = (purchase_id) => {
    // This is now handled by the useApi hook
    // The purchase details will be automatically fetched when purchase_id changes
  }

  const getAllSuppliers = () => {
    // Trigger the suppliers API call
    // The useApi hook will handle the rest
  }

  const handleQuantityChange = (e, index) => {
    const data = { ...newPurchase }
    const value = e.target.value.replace(/^0+/, '')
    data.purchase_items[index].received_count = value
    setNewPurchase(data)
  }

  const handlePurchasePriceChange = (e, index) => {
    const data = { ...newPurchase }
    const value = e.target.value.replace(/^0+/, '')
    data.purchase_items[index].purchase_price = value
    setNewPurchase(data)
  }

  const addPurchase = () => {
    if (
      newPurchase.purchase_items
        .map((item) => item.received_count)
        .some((item) => item === 0)
    ) {
      showFailure('Please enter the quantity received')
      return
    }
    const setting = {
      warehouse_id: newPurchase.warehouse?.id,
      products: newPurchase.purchase_items.map((item) => {
        return {
          ...item,
          quantity: Number(item.received_count),
          purchase_price: item.purchase_price ? item.purchase_price : 0,
          product_id: item.product?.product_id,
          purchase_item_id: null,
          variation_id: null,
          stock_quantity: 0,
          sku: item.product?.product_sku,
        }
      }),
      notes: newPurchase.notes,
      supplier_id: newPurchase.supplier?.id,
      total_amount: TotalCost(),
      reference_number: newPurchase.reference_number,
      status: 'Draft',
    }
    if (setting?.products?.length === 0) {
      showFailure('No product added')
    } else {
      showPageLoader()
      createPurchaseOrder(setting).subscribe((res) => {
        const response = res.response as {
          message: string
          error?:
            | string
            | {
                error: string
                field?: string
                details?: Array<{ product_id: number; errors: string[] }>
              }
        }
        hidePageLoader()
        if (response.message) {
          setDetailType('new')
          setPurchaseDetail({} as IPurchaseDetails)
          setShowDetailModal(false)

          showSuccess('Purchase created successfully')
          setReload(true)

          setError({
            error: '',
            field: '',
          })
        } else {
          if (response.error) {
            let errorMessage = ''
            let errorField = ''

            // Handle complex error structure with details
            if (typeof response.error === 'object' && response.error.details) {
              errorMessage = response.error.error || 'Validation failed'
              const details = response.error.details
                .map(
                  (detail) =>
                    `Product ID ${detail.product_id}: ${detail.errors.join(
                      ', ',
                    )}`,
                )
                .join('; ')
              errorMessage += ` - ${details}`
              errorField = response.error.field || ''
            } else if (typeof response.error === 'object') {
              errorMessage = response.error.error || 'An error occurred'
              errorField = response.error.field || ''
            } else {
              errorMessage = response.error
            }

            setError({
              error: errorMessage,
              field: errorField,
            })

            if (errorField) {
              showFailure(`${errorField}: ${errorMessage}`)
            } else {
              showFailure(errorMessage)
            }
          } else {
            showFailure('An error occurred while creating the purchase')
          }
        }
      })
    }
  }

  const totalItems = () => {
    const value = newPurchase.purchase_items?.reduce((acc, curr) => {
      return acc + Number(curr.received_count || 0)
    }, 0)
    return value
  }

  const discardChanges = () => {
    pixelConfirm({
      title: 'Confirmation',
      message: 'Are you sure you want to discard changes',
      onConfirm: () => {
        setFormEditAble(false)
        setDetailType('return')
        setPurchaseId(purchase_id)
      },
    })
  }

  const totalPrice = (item) => {
    const value =
      Number(item?.received_count) * Number(item?.purchase_price) || 0
    return value.toFixed(2)
  }

  const currentWarehouse = () => {
    const warehouses = warehouseList?.map((item: IWarehouse) => {
      if (item.wh_id === newPurchase.warehouse?.wh_id) {
        return item.name.replace(/,/g, ' ')
      }
    })
    return warehouses
  }

  const debounceFn = React.useCallback(
    _.debounce((value) => {
      if (value.length > 0) setShowSearchProduct(true)
    }, 1000),
    [searchValue],
  )

  // Effects
  React.useEffect(() => {
    if (showScanner) {
      // Small delay to ensure modal is rendered before starting scanner
      const timer = setTimeout(() => {
        startScanner()
      }, 100)
      return () => clearTimeout(timer)
    } else {
      stopScanner()
    }
  }, [showScanner])

  React.useEffect(() => {
    if (detailType === 'new') {
      setNewPurchase({
        ...newPurchase,
        id: 0,
        ems_customer_id: 0,
        reference_number: '',
        total_amount: null,
        notes: '',
        user: 0,
        created_at: {
          date: '',
          timezone_type: 0,
          timezone: '',
        },
        is_final_status: false,
        created_by: 0,
        created_by_id: 0,
        supplier: {
          id: 0,
          supplier_name: '',
          contact_person: '',
          phone: '',
        },
        purchase_status: {
          id: 0,
          status: '',
          description: '',
          is_final_status: false,
          is_default: false,
        },
        warehouse: {
          wh_id: warehouse_id ? Number(warehouse_id) : 0,
          name: '',
        },
        purchase_items: [],
      })
    }
  }, [detailType])

  React.useEffect(() => {
    if (warehouseList?.length === 0) {
      setWarehouseList(state?.warehouses || [])
    }
  }, [state])

  React.useEffect(() => {
    if (purchase_id) {
      setNewPurchase({
        ...newPurchase,
        purchase_items: purchaseDetail?.purchase_items?.map((item) => {
          return {
            ...item,
            received_count:
              Number(item.received_count) === 0 ? 1 : item.received_count,
          }
        }),
        id: purchaseDetail?.id,
        reference_number: purchaseDetail?.reference_number,
        notes: purchaseDetail?.notes,
        created_at: purchaseDetail?.created_at,
        total_amount: purchaseDetail?.total_amount,
        is_final_status: purchaseDetail?.is_final_status,
        created_by: purchaseDetail?.created_by,
        created_by_id: purchaseDetail?.created_by_id,
        supplier: purchaseDetail?.supplier,
        purchase_status: purchaseDetail?.purchase_status,
        warehouse: purchaseDetail?.warehouse,
        type: 'update',
      })
    }
  }, [purchaseDetail])

  React.useEffect(() => {
    if (purchase_id == undefined || purchase_id === null) {
      setFormEditAble(true)
      // Trigger API calls for suppliers and categories
      // These will be handled by the useApi hooks
    } else {
      setFormEditAble(false)
    }

    // Purchase details are automatically fetched by the useApi hook
  }, [purchase_id])

  React.useEffect(() => {
    if (
      window.location.pathname.includes(
        `/inventory/purchases/${purchase_id}/edit`,
      )
    ) {
      setFormEditAble(true)
      // API calls are handled by useApi hooks
    }
  }, [])

  return {
    // State
    formEditAble,
    setFormEditAble,
    showScanner,
    setShowScanner,
    showFileUpload,
    setShowFileUpload,
    showUploadModal,
    setShowUploadModal,
    scannedSKU,
    setScannedSKU,
    uploadedFile,
    setUploadedFile,
    uploadProgress,
    setUploadProgress,
    uploadResults,
    setUploadResults,
    addNewSupplierModal,
    setAddNewSupplierModal,
    purchaseRef,
    fileInputRef,
    scannerRef,
    showSearchProduct,
    setShowSearchProduct,
    suppliers,
    setSuppliers,
    searchValue,
    setSearchValue,
    purchaseDetail,
    setPurchaseDetail,
    warehouseList,
    setWarehouseList,
    newPurchase,
    setNewPurchase,
    error,
    setError,
    categories,
    setCategories,
    tenant_info,

    // Loading states from useApi hooks
    categoriesLoading,
    suppliersLoading,
    purchaseDetailsLoading,

    // Functions
    startScanner,
    stopScanner,
    handleScan,
    validateScannedProduct,
    handleFileUpload,
    handleFileUploadFromModal,
    downloadTemplate,
    processUploadedFile,
    validateProductsFromAPI,
    getAllCategories,
    TotalCost,
    updatePurchase,
    updatePurchasesStatus,
    fetchPurchaseDetails,
    getAllSuppliers,
    handleQuantityChange,
    handlePurchasePriceChange,
    addPurchase,
    totalItems,
    discardChanges,
    totalPrice,
    currentWarehouse,
    debounceFn,
    showSuccess,
    showFailure,
    showPageLoader,
    hidePageLoader,
    showLoader,
    hideLoader,
  }
}
